/**
 * База знаний — рабочее место управляющего (§10.4, §10.6 ТЗ сервиса).
 *
 * Три части, и они не случайно на одном экране: это один цикл.
 *
 * 1. **Регламенты** — то, из чего помощник отвечает. Пока их нет, он не
 *    отвечает ни на что, и это главное, что управляющий должен понять,
 *    открыв экран впервые.
 * 2. **Пул пробелов** — вопросы, на которые база не ответила. Ответ
 *    управляющего становится частью базы и достаётся следующему
 *    спросившему: провал системы превращается в её рост (§10.4). Это
 *    единственный реалистичный способ вытащить знания, которые сегодня
 *    существуют только в головах.
 * 3. **Стоп-темы** — о чём помощник не рассуждает никогда (D54). Список
 *    показан, потому что иначе поведение выглядит поломкой: «спросил про
 *    возврат — он не ответил».
 *
 * Права проверяет сервер на каждом запросе. Спрятанная вкладка защитой
 * не является — `403` придёт и тому, кто откроет адрес руками.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  Button,
  Card,
  Empty,
  ErrorState,
  Field,
  Loading,
  formatDateTime,
} from '@shell/index';
import { studyApi } from '../api';
import type { GapRow } from '../api';

/** Что делает стоп-тема, словами. Код поведения — из модели данных, а §7
 *  правил понятного интерфейса запрещает показывать его человеку. */
const BEHAVIOUR: Record<string, string> = {
  redirect: 'уводит в справочник',
  quote_only: 'цитирует документ дословно, не пересказывая',
  escalate: 'передаёт вопрос вам',
};

/**
 * Склонение существительного при числе: «1 фрагмент», «2 фрагмента»,
 * «42 фрагмента», «5 фрагментов».
 *
 * Мелочь, но §7 правил понятного интерфейса — про язык, и «разобран на 42
 * фрагментов» читается как недоделка ровно так же, как `escalate` вместо
 * «передаёт вопрос вам».
 */
function plural(count: number, one: string, few: string, many: string): string {
  const mod100 = count % 100;
  if (mod100 >= 11 && mod100 <= 14) {
    return many;
  }
  const mod10 = count % 10;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

function failureText(error: unknown): string {
  if (error instanceof ApiError && error.detail) {
    return error.detail;
  }
  return 'Не получилось — попробуйте ещё раз. Если повторится, это сбой сервера.';
}

function Documents() {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const documents = useQuery({ queryKey: ['documents'], queryFn: studyApi.documents });

  const upload = useMutation({
    mutationFn: (file: File) => studyApi.uploadDocument(file, title.trim()),
    onSuccess: (result) => {
      setTitle('');
      setNotice(
        `«${result.title}» загружен и разобран на ${result.chunks} ` +
          `${plural(result.chunks, 'фрагмент', 'фрагмента', 'фрагментов')}. ` +
          'Помощник уже отвечает по нему.',
      );
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (error) => setNotice(failureText(error)),
  });

  const reindex = useMutation({
    mutationFn: (id: number) => studyApi.reindexDocument(id),
    onSuccess: (result) => {
      setNotice(
        `Переиндексирован: ${result.chunks} ` +
          `${plural(result.chunks, 'фрагмент', 'фрагмента', 'фрагментов')}.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (error) => setNotice(failureText(error)),
  });

  const archive = useMutation({
    mutationFn: (id: number) => studyApi.archiveDocument(id),
    onSuccess: () => {
      setNotice('Снят с публикации. Ответы из него больше не приходят.');
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (error) => setNotice(failureText(error)),
  });

  if (documents.isLoading) {
    return <Loading />;
  }
  if (documents.isError) {
    return (
      <ErrorState title="Не удалось загрузить список регламентов" error={documents.error} />
    );
  }

  const rows = documents.data?.documents ?? [];
  const live = rows.filter((row) => row.status !== 'archived');

  return (
    <Card title="Регламенты">
      <Field label="Название — как его назовут сотрудники">
        <input
          className="search"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Например: Стандарты обслуживания зала"
        />
      </Field>

      <Field label="Файл — Word или PDF с текстом">
        <input
          type="file"
          accept=".doc,.docx,.pdf,.txt,.md"
          disabled={upload.isPending}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              upload.mutate(file);
            }
            // Сбрасываем поле: иначе повторная загрузка того же файла
            // после исправления ошибки не вызовет `change` и кнопка
            // будет выглядеть сломанной (§8 правил).
            event.target.value = '';
          }}
        />
      </Field>

      {upload.isPending && <p className="faint">Разбираю документ — это занимает несколько секунд.</p>}
      {notice && <p className="answer">{notice}</p>}

      {live.length === 0 ? (
        // §2 правил: что пусто, почему и что сделать — все три части.
        // Причина здесь настоящая и важная: пустая база означает, что
        // помощник не работает вовсе, а не что «пока нет данных».
        <Empty
          title="Регламентов пока нет"
          text={
            <>
              Пока база пуста, ИИ-помощник не отвечает ни на один вопрос: отвечать
              ему не из чего.
              <br />
              Загрузите первый документ полем выше — подойдут стандарты обслуживания,
              правила брони, инструкции. PDF должен быть текстовым: скан помощник
              прочитать не может и скажет об этом при загрузке.
            </>
          }
        />
      ) : (
        <ul className="doc-list">
          {live.map((row) => (
            <li key={row.id}>
              <div>
                {row.is_demo && <span className="demo-badge">ДЕМО</span>}
                <strong>{row.title}</strong>{' '}
                <span className="faint">
                  версия {row.version} · загружен {formatDateTime(row.uploaded_at)}
                </span>
              </div>
              <div className="faint">
                {row.indexed
                  ? 'участвует в ответах'
                  : 'загружен, но в ответах не участвует — нажмите «Переиндексировать»'}
              </div>
              <div className="chips">
                <Button small variant="quiet" onClick={() => reindex.mutate(row.id)}>
                  Переиндексировать
                </Button>
                <Button small variant="quiet" onClick={() => archive.mutate(row.id)}>
                  Снять с публикации
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Gap({ row }: { row: GapRow }) {
  const queryClient = useQueryClient();
  const [answer, setAnswer] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['gaps'] });

  const reply = useMutation({
    mutationFn: () => studyApi.answerGap(row.id, answer.trim()),
    onSuccess: refresh,
    onError: (error) => setFailure(failureText(error)),
  });

  const dismiss = useMutation({
    mutationFn: () => studyApi.dismissGap(row.id),
    onSuccess: refresh,
    onError: (error) => setFailure(failureText(error)),
  });

  const text = answer.trim();

  return (
    <li>
      <div>
        <strong>{row.question}</strong>
      </div>
      <div className="faint">спросили {formatDateTime(row.asked_at)}</div>
      <textarea
        className="search ask"
        rows={2}
        value={answer}
        onChange={(event) => setAnswer(event.target.value)}
        placeholder="Ответ попадёт в базу и достанется следующему, кто спросит"
      />
      <div className="chips">
        <Button
          small
          variant="primary"
          disabled={!text || reply.isPending}
          onClick={() => reply.mutate()}
        >
          Ответить и внести в базу
        </Button>
        <Button small variant="quiet" disabled={dismiss.isPending} onClick={() => dismiss.mutate()}>
          Это не вопрос к базе
        </Button>
      </div>
      {failure && <p className="answer failure">{failure}</p>}
    </li>
  );
}

function Gaps() {
  const gaps = useQuery({ queryKey: ['gaps'], queryFn: () => studyApi.gaps('open') });

  if (gaps.isLoading) {
    return <Loading />;
  }
  if (gaps.isError) {
    return <ErrorState title="Не удалось загрузить пробелы" error={gaps.error} />;
  }

  const rows = gaps.data?.gaps ?? [];

  return (
    <Card title="Вопросы без ответа">
      {rows.length === 0 ? (
        // Пусто здесь — хорошая новость, и сказать это надо прямо:
        // иначе управляющий решит, что раздел не работает.
        <Empty
          title="Пока таких вопросов нет"
          text="Сюда попадают вопросы, на которые помощник не нашёл ответа в регламентах. Пусто — значит, база покрывает то, о чём спрашивают. Заходить сюда стоит раз в неделю."
        />
      ) : (
        <ul className="gap-list">
          {rows.map((row) => (
            <Gap key={row.id} row={row} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function StopTopics() {
  const topics = useQuery({ queryKey: ['stop-topics'], queryFn: studyApi.stopTopics });

  if (topics.isLoading || topics.isError) {
    // Список справочный: пока он не пришёл, экран полезен и без него, а
    // красная плашка на вспомогательном блоке пугает зря.
    return null;
  }

  const rows = topics.data?.stop_topics ?? [];
  if (rows.length === 0) {
    return null;
  }

  return (
    <Card title="О чём помощник не рассуждает">
      <p className="faint">
        В этих темах выдуманный ответ стоит денег или здоровья гостя, поэтому
        помощник до модели их не доводит.
      </p>
      <ul className="doc-list">
        {rows.map((row) => (
          <li key={row.code}>
            <strong>{row.name}</strong>{' '}
            <span className="faint">
              — {BEHAVIOUR[row.behaviour] ?? row.behaviour}
              {row.is_urgent ? '; проходит даже в тихие часы' : ''}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function Knowledge() {
  return (
    <>
      <Documents />
      <Gaps />
      <StopTopics />
    </>
  );
}
